import { graphql } from "@/gql";
import PatternImage, { getPatternImageUrl } from "@/components/PatternImage";
import type { GetAdminPixelArtsQuery } from "@/gql/graphql";
import { useQuery } from "@/utils/graphql";
import { normalizePatternData } from "@/utils/patternRenderer";
import { formatTime } from "@/utils/time";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const GET_ALL_PIXEL_ARTS = graphql(`
  query GetAdminPixelArts(
    $limit: Int!
    $page: Int!
    $filter: PixelArtFilterInput!
    $having: PixelArtHavingInput!
  ) {
    pixelArts(
      pagination: { page: { limit: $limit, page: $page } }
      filters: $filter
      having: $having
    ) {
      nodes {
        pixelArtId
        userId
        paletteName
        width
        height
        createdOn
        patternImageUrl
        patternGridJson
        patternLegendJson
        patternMetaJson
        user {
          id
          name
        }
      }
      paginationInfo {
        total
      }
    }
  }
`);

export default function AdminPixelArtPage() {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });
  const [searchUsername, setSearchUsername] = useState<string>("");
  const [previewArt, setPreviewArt] = useState<
    GetAdminPixelArtsQuery["pixelArts"]["nodes"][0] | null
  >(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data, error, isLoading, refetch } = useQuery(GET_ALL_PIXEL_ARTS, {
    limit: pagination.pageSize || 10,
    page: (pagination.current || 1) - 1,
    filter: {},
    having: searchUsername.trim()
      ? { user: { name: { ci_eq: searchUsername.trim() } } }
      : {},
  });

  const pixelArts = data?.pixelArts?.nodes || [];
  const totalCount = data?.pixelArts?.paginationInfo?.total || 0;

  const handleDownload = (
    record: GetAdminPixelArtsQuery["pixelArts"]["nodes"][0],
  ) => {
    const pattern = normalizePatternData({
      width: record.width,
      height: record.height,
      patternGridJson: record.patternGridJson,
      patternLegendJson: record.patternLegendJson,
      patternMetaJson: record.patternMetaJson,
    });
    const imageUrl = pattern
      ? getPatternImageUrl({
          ...pattern,
          title: `${record.width}×${record.height}`,
          fallbackUrl: record.patternImageUrl,
        })
      : record.patternImageUrl;
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `pixel-art-${record.pixelArtId}.png`;
    link.click();
  };

  const handleDelete = async (pixelArtId: string) => {
    // TODO: Implement delete API call
    console.log("Delete pixel art:", pixelArtId);
    // await deletePixelArt(pixelArtId);
    // refetch();
  };

  const handlePreview = (
    record: GetAdminPixelArtsQuery["pixelArts"]["nodes"][0],
  ) => {
    setPreviewArt(record);
    setIsModalVisible(true);
  };

  const columns: ColumnsType<GetAdminPixelArtsQuery["pixelArts"]["nodes"][0]> =
    [
      {
        title: t("pixelArt.userId"),
        dataIndex: ["user", "id"],
        key: "userId",
        width: 100,
        render: (userId) => userId || "-",
      },
      {
        title: t("pixelArt.user"),
        dataIndex: ["user", "name"],
        key: "userName",
        width: 150,
        render: (name) => name || "-",
      },
      {
        title: t("pixelArt.dimensions"),
        dataIndex: "width",
        key: "dimensions",
        width: 120,
        render: (
          width,
          record: GetAdminPixelArtsQuery["pixelArts"]["nodes"][0],
        ) => `${width}×${record.height}`,
      },
      {
        title: t("pixelArt.palette"),
        dataIndex: "paletteName",
        key: "palette",
        width: 150,
        render: (palette) => palette || "-",
      },
      {
        title: t("common.createdAt"),
        dataIndex: "createdOn",
        key: "createdOn",
        width: 160,
        defaultSortOrder: "descend",
      },
      {
        title: t("common.actions"),
        key: "actions",
        width: 180,
        fixed: "right",
        render: (
          _,
          record: GetAdminPixelArtsQuery["pixelArts"]["nodes"][0],
        ) => (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
            >
              {t("pixelArt.viewDetails")}
            </Button>
            {(record.patternImageUrl || record.patternGridJson) && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
              >
                {t("pixelArt.downloadBtn")}
              </Button>
            )}
            <Popconfirm
              title={t("pixelArt.confirmDelete")}
              onConfirm={() => handleDelete(record.pixelArtId as string)}
            >
              <Button danger size="small" icon={<DeleteOutlined />}>
                {t("pixelArt.delete")}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="brand-surface-hero" variant="outlined">
        <div className="space-y-2">
          <Typography.Title level={2} className="m-0">
            {t("pixelArt.title")}
          </Typography.Title>
        </div>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          type="error"
          showIcon
          message={`${t("common.error")}: ${error.message}`}
          closable
        />
      )}

      {/* Search Bar */}
      <Card className="brand-surface-card" variant="outlined">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1 min-w-0">
            <label className="block text-sm font-medium mb-2">
              {t("pixelArt.search")}
            </label>
            <Input
              placeholder={t("pixelArt.username")}
              value={searchUsername}
              onChange={(e) => {
                setSearchUsername(e.target.value);
                setPagination({ ...pagination, current: 1 });
              }}
              allowClear
            />
          </div>
          <Button type="primary" onClick={() => refetch()}>
            {t("common.search")}
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="brand-surface-card" variant="outlined">
        <Spin spinning={isLoading}>
          {pixelArts.length === 0 ? (
            <Empty description={t("pixelArt.emptyState")} />
          ) : (
            <Table
              columns={columns}
              dataSource={pixelArts.map((art) => ({
                ...art,
                key: art.pixelArtId,
              }))}
              pagination={{
                current: pagination.current || 1,
                pageSize: pagination.pageSize || 10,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (total) =>
                  `${t("common.total")} ${total} ${t("common.actions")}`,
              }}
              onChange={(pag) => setPagination(pag)}
              scroll={{ x: 1200 }}
            />
          )}
        </Spin>
      </Card>

      {/* Preview Modal */}
      <Modal
        title={t("pixelArt.viewDetails")}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        {previewArt && (
          <div className="space-y-4">
            {(previewArt.patternImageUrl || previewArt.patternGridJson) && (
              <PatternImage
                patternImageUrl={previewArt.patternImageUrl}
                patternGridJson={previewArt.patternGridJson}
                patternLegendJson={previewArt.patternLegendJson}
                patternMetaJson={previewArt.patternMetaJson}
                width={previewArt.width}
                height={previewArt.height}
                alt={`Pixel art ${previewArt.pixelArtId}`}
                style={{ width: "100%", borderRadius: 8 }}
                previewMask={t("common.preview")}
              />
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  {t("common.id")}
                </label>
                <p className="text-sm">{previewArt.pixelArtId}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  {t("pixelArt.user")}
                </label>
                <p className="text-sm">
                  {previewArt.user?.name} (ID: {previewArt.userId})
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  {t("pixelArt.dimensions")}
                </label>
                <p className="text-sm">
                  {previewArt.width} × {previewArt.height}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  {t("pixelArt.palette")}
                </label>
                <p className="text-sm">{previewArt.paletteName || "-"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  {t("common.createdAt")}
                </label>
                <p className="text-sm">{formatTime(previewArt.createdOn)}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
